import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  inject,
  Injectable,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';

@Injectable()
export class TabActivityService implements OnDestroy {
  private ngZone = inject(NgZone);

  private lastActivity = signal<number>(Date.now());
  public readonly lastActivitySignal = computed(() => this.lastActivity());
  private destroy$ = new Subject<void>();

  get lastActivityTime(): number {
    let lastActivity = 0;

    try {
      lastActivity = Number(localStorage.getItem('lastActivity'));
    } catch {
      lastActivity = -1;
    }

    return lastActivity > 0 ? lastActivity : this.lastActivity();
  }

  startMonitoring(): void {
    const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    if (!isBrowser) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const events = ['mousemove', 'touchstart', 'keydown', 'click', 'scroll'];

      events.forEach((event) => {
        fromEvent(window, event)
          .pipe(debounceTime(300), takeUntil(this.destroy$))
          .subscribe(() => this.updateLastActivity());
      });
    });
  }

  isActive(timeout: number): boolean {
    return Date.now() - this.lastActivityTime < timeout;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    localStorage.removeItem('lastActivity');
  }

  private updateLastActivity(): void {
    this.ngZone.run(() => {
      this.lastActivity.set(Date.now());

      try {
        localStorage.setItem('lastActivity', this.lastActivity().toString());
      } catch {
        console.warn('WARNING: Unable to update last activity.');
      }
    });
  }
}
